import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { InputWrapper } from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";
import {
  DropdownButton,
  DropdownItem,
  DropdownList,
} from "common/components/Field/new/DropdownComponents";
import { TextInputWithoutValid } from "common/components/Input/TextInputWithoutValid";
import { useDynamicDropdownList } from "common/hooks/useDynamicDropdownList";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "common/i18n/keys";

const NoResultsMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--color-text-secondary, #666);
  font-size: var(--fontSize-14);
`;

export const ComboBox = ({
  label,
  caption,
  icon,
  name,
  placeholder,
  searchPlaceholder,
  defaultValue,
  currentItem,
  itemList,
  required,
  onChange,
  disabled,
  forceClose,
  noResultsText = "No results",
  ...rest
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const findItemFromList = (target) => {
    const targetValue = typeof target === "object" ? target?.value : target;
    return itemList?.find((item) => item?.value === targetValue);
  };

  const initialValue = currentItem ?? defaultValue;
  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name,
    rules: { required: required || false },
    defaultValue: initialValue,
  });

  const selectedItem = useMemo(() => {
    if (currentItem != null) {
      return findItemFromList(currentItem);
    }
    return findItemFromList(inputProps.value);
  }, [currentItem, inputProps.value, itemList]);

  const filteredItemList = useMemo(() => {
    if (!searchQuery.trim()) return itemList;
    const query = searchQuery.toLowerCase().trim();
    return itemList?.filter((item) => {
      const text = String(item?.text ?? "").toLowerCase();
      const val = String(item?.value ?? "").toLowerCase();

      const fuzzyMatch = (str, pattern) => {
        let patternIdx = 0;
        for (let i = 0; i < str.length && patternIdx < pattern.length; i++) {
          if (str[i] === pattern[patternIdx]) {
            patternIdx++;
          }
        }
        return patternIdx === pattern.length;
      };

      return fuzzyMatch(text, query) || fuzzyMatch(val, query);
    });
  }, [itemList, searchQuery]);

  const { dropdownListStyle, updatePosition } = useDynamicDropdownList(
    dropdownRef,
    {
      itemCount: filteredItemList?.length || 0,
      open,
      listRef,
    },
  );

  const handleSelect = (option) => {
    setOpen(false);
    setSearchQuery("");
    onChange?.(option);
    if (currentItem == null) {
      onValueChange(option.value);
    }
  };

  // 강제 닫기 이벤트
  useEffect(() => {
    if (forceClose) {
      setOpen(false);
      setSearchQuery("");
    }
  }, [forceClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  const handleToggle = () => {
    updatePosition();
    setOpen((prev) => !prev);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && filteredItemList?.length > 0) {
      handleSelect(filteredItemList[0]);
    }
    if (e.key === "Escape") {
      setOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <InputWrapper
      ref={dropdownRef}
      label={label}
      caption={caption}
      icon={icon}
      rules={{ required }}
      error={error}
      {...rest}>
      {open ? (
        <TextInputWithoutValid
          ref={searchInputRef}
          placeholder={
            searchPlaceholder ?? t(I18N_KEYS.component.comboBox.placeholder)
          }
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={handleSearchKeyDown}
        />
      ) : (
        <DropdownButton
          component={selectedItem?.component}
          text={selectedItem?.text ?? ""}
          placeholder={placeholder}
          onClick={handleToggle}
          isListOpen={open}
          error={error}
          disabled={disabled}
        />
      )}
      {open && (
        <DropdownList ref={listRef} style={dropdownListStyle}>
          {filteredItemList?.length > 0 ? (
            filteredItemList.map((item, index) => (
              <DropdownItem
                key={`combobox-item-${name}-${index}`}
                item={item}
                onClick={() => handleSelect(item)}
                selected={selectedItem?.key === item.key}
              />
            ))
          ) : (
            <NoResultsMessage>{noResultsText}</NoResultsMessage>
          )}
        </DropdownList>
      )}
    </InputWrapper>
  );
};
